import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  PoundSterling,
  Plus,
  RefreshCw,
  CheckCircle2,
  FileText,
  TrendingUp,
  TrendingDown,
  PiggyBank,
  Receipt,
  Users,
  Search,
  Filter,
  Download,
  Calendar,
  CreditCard,
  Banknote,
  X,
  Edit,
  Eye,
  Printer,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Wheat,
  FileSpreadsheet,
  Image as ImageIcon,
  Mail
} from 'lucide-react';
import FinancialReportsSection from './FinancialReportsSection';
import DepartmentEmailModule from './DepartmentEmailModule';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

// Sub-tabs for finance section
const FINANCE_TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'harvest', label: 'Harvest', icon: Wheat },
  { id: 'income', label: 'Income', icon: TrendingUp },
  { id: 'expenses', label: 'Expenses', icon: TrendingDown },
  { id: 'reports', label: 'Reports', icon: FileSpreadsheet },
  { id: 'audit', label: 'Audit Log', icon: FileSpreadsheet, mainAdminOnly: true },
  { id: 'email', label: 'Email', icon: Mail }
];

// Income categories
const INCOME_CATEGORIES = [
  { value: 'tithe', label: 'Tithe', color: 'emerald' },
  { value: 'offering', label: 'Offering', color: 'blue' },
  { value: 'donation', label: 'Donation', color: 'purple' },
  { value: 'program', label: 'Program', color: 'amber' },
  { value: 'other', label: 'Other', color: 'slate' }
];

// Expense categories
const EXPENSE_CATEGORIES = [
  { value: 'rent', label: 'Rent', color: 'red' },
  { value: 'utilities', label: 'Utilities', color: 'orange' },
  { value: 'welfare', label: 'Welfare', color: 'pink' },
  { value: 'evangelism', label: 'Evangelism', color: 'indigo' },
  { value: 'maintenance', label: 'Maintenance', color: 'yellow' },
  { value: 'other', label: 'Other', color: 'slate' }
];

// Financial Dashboard Component
const FinanceDashboard = ({ data, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const summary = data?.summary || {};
  
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white/20">
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="text-emerald-100 text-sm">Total Income</span>
          </div>
          <p className="text-3xl font-bold">£{(summary.grand_total_income || 0).toLocaleString()}</p>
          <p className="text-emerald-100 text-sm mt-2">Regular: £{(summary.total_income || 0).toLocaleString()}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white/20">
              <Wheat className="w-6 h-6" />
            </div>
            <span className="text-amber-100 text-sm">Harvest</span>
          </div>
          <p className="text-3xl font-bold">£{(summary.total_harvest || 0).toLocaleString()}</p>
          <p className="text-amber-100 text-sm mt-2">{data?.harvest?.count || 0} contributions</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white/20">
              <TrendingDown className="w-6 h-6" />
            </div>
            <span className="text-red-100 text-sm">Expenses</span>
          </div>
          <p className="text-3xl font-bold">£{(summary.total_expenses || 0).toLocaleString()}</p>
          <p className="text-red-100 text-sm mt-2">{data?.expenses?.count || 0} records</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={`p-6 rounded-2xl shadow-lg text-white ${
            (summary.net_balance || 0) >= 0 
              ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
              : 'bg-gradient-to-br from-slate-600 to-slate-800'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white/20">
              <PiggyBank className="w-6 h-6" />
            </div>
            <span className="text-blue-100 text-sm">Net Balance</span>
          </div>
          <p className="text-3xl font-bold">£{(summary.net_balance || 0).toLocaleString()}</p>
          <p className="text-blue-100 text-sm mt-2">
            {(summary.net_balance || 0) >= 0 ? 'Surplus' : 'Deficit'}
          </p>
        </motion.div>
      </div>

      {/* Income Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
        >
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Income by Category
          </h3>
          <div className="space-y-3">
            {INCOME_CATEGORIES.map(cat => {
              const amount = data?.income?.by_category?.[cat.value] || 0;
              const total = summary.total_income || 1;
              const percentage = ((amount / total) * 100) || 0;
              return (
                <div key={cat.value} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{cat.label}</span>
                    <span className="font-semibold text-slate-800">£{amount.toLocaleString()}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      className={`h-full bg-${cat.color}-500 rounded-full`}
                      style={{ backgroundColor: `var(--${cat.color}-500, #10b981)` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
        >
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-500" />
            Expenses by Category
          </h3>
          <div className="space-y-3">
            {EXPENSE_CATEGORIES.map(cat => {
              const amount = data?.expenses?.by_category?.[cat.value] || 0;
              const total = summary.total_expenses || 1;
              const percentage = ((amount / total) * 100) || 0;
              return (
                <div key={cat.value} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{cat.label}</span>
                    <span className="font-semibold text-slate-800">£{amount.toLocaleString()}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      className="h-full bg-red-500 rounded-full"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Harvest Breakdown */}
      {data?.harvest?.by_type && Object.keys(data.harvest.by_type).length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
        >
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Wheat className="w-5 h-5 text-amber-500" />
            Harvest Contributions
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data.harvest.by_type).map(([type, amount]) => (
              <div key={type} className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-700 capitalize">{type}</p>
                <p className="text-xl font-bold text-amber-900">£{amount.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Harvest Management Component
const HarvestManagement = ({ adminUser }) => {
  const [activeTab, setActiveTab] = useState('events');
  const [harvestEvents, setHarvestEvents] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [guestContributors, setGuestContributors] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showContributionForm, setShowContributionForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [receiptModal, setReceiptModal] = useState(null);
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [newEvent, setNewEvent] = useState({
    name: '',
    harvest_type: 'adult',
    date: new Date().toISOString().split('T')[0],
    description: '',
    target_amount: ''
  });
  
  const [newContribution, setNewContribution] = useState({
    harvest_event_id: '',
    harvest_type: 'adult',
    contributor_type: 'member',
    contributor_id: '',
    contributor_name: '',
    contributor_email: '',
    amount: '',
    payment_method: 'cash',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsRes, contributionsRes, guestsRes, membersRes] = await Promise.all([
        axios.get(`${API}/harvest-events`),
        axios.get(`${API}/harvest-contributions`),
        axios.get(`${API}/guest-contributors`),
        axios.get(`${API}/members`)
      ]);
      setHarvestEvents(eventsRes.data);
      setContributions(contributionsRes.data);
      setGuestContributors(guestsRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Error loading harvest data:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateEvent = async () => {
    if (!newEvent.name || !newEvent.date) return;
    try {
      await axios.post(`${API}/harvest-events`, {
        ...newEvent,
        target_amount: parseFloat(newEvent.target_amount) || 0
      });
      setNewEvent({ name: '', harvest_type: 'adult', date: new Date().toISOString().split('T')[0], description: '', target_amount: '' });
      setShowEventForm(false);
      loadData();
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  const handleCreateContribution = async () => {
    if (!newContribution.contributor_name || !newContribution.amount) return;
    try {
      const response = await axios.post(`${API}/harvest-contributions`, {
        ...newContribution,
        amount: parseFloat(newContribution.amount),
        received_by_id: adminUser?.id || '',
        received_by_name: adminUser?.full_name || 'Admin'
      });
      
      // Show receipt modal
      setReceiptModal({
        contribution: response.data.contribution,
        mini_statement: response.data.mini_statement,
        email_sent: response.data.email_sent
      });
      
      setNewContribution({
        harvest_event_id: '',
        harvest_type: 'adult',
        contributor_type: 'member',
        contributor_id: '',
        contributor_name: '',
        amount: '',
        payment_method: 'cash',
        notes: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowContributionForm(false);
      loadData();
    } catch (error) {
      console.error('Error creating contribution:', error);
    }
  };

  const handleCreateGuestContributor = async () => {
    if (!newContribution.contributor_name) return;
    try {
      const response = await axios.post(`${API}/guest-contributors`, {
        full_name: newContribution.contributor_name,
        email: newContribution.contributor_email || ''
      });
      setGuestContributors(prev => [response.data.contributor, ...prev]);
      setNewContribution(prev => ({
        ...prev,
        contributor_id: response.data.contributor.id
      }));
    } catch (error) {
      console.error('Error creating guest contributor:', error);
    }
  };

  const filteredContributions = contributions.filter(c => 
    c.contributor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.receipt_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
              <Wheat className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Harvest Management</h2>
              <p className="text-sm text-slate-500">Manage harvest events and contributions</p>
            </div>
          </div>
          <div className="flex gap-2">
            <motion.button
              onClick={() => setShowEventForm(true)}
              className="px-4 py-2 rounded-xl bg-amber-100 text-amber-700 font-medium hover:bg-amber-200 transition-all flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-4 h-4" />
              New Event
            </motion.button>
            <motion.button
              onClick={() => setShowContributionForm(true)}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium shadow-lg flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-4 h-4" />
              Record Contribution
            </motion.button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-2 mt-6">
          {['events', 'contributions'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-xl font-medium capitalize transition-all ${
                activeTab === tab
                  ? 'bg-amber-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Event Form Modal */}
      <AnimatePresence>
        {showEventForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEventForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Create Harvest Event</h3>
                <button onClick={() => setShowEventForm(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Event Name"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newEvent.name}
                  onChange={e => setNewEvent(prev => ({ ...prev, name: e.target.value }))}
                />
                <select
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newEvent.harvest_type}
                  onChange={e => setNewEvent(prev => ({ ...prev, harvest_type: e.target.value }))}
                >
                  <option value="adult">Adult Harvest</option>
                  <option value="juvenile">Juvenile Harvest</option>
                </select>
                <input
                  type="date"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newEvent.date}
                  onChange={e => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                />
                <input
                  type="number"
                  placeholder="Target Amount (optional)"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newEvent.target_amount}
                  onChange={e => setNewEvent(prev => ({ ...prev, target_amount: e.target.value }))}
                />
                <textarea
                  placeholder="Description (optional)"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  rows={3}
                  value={newEvent.description}
                  onChange={e => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                />
                <motion.button
                  onClick={handleCreateEvent}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Create Event
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contribution Form Modal */}
      <AnimatePresence>
        {showContributionForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowContributionForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Record Contribution</h3>
                <button onClick={() => setShowContributionForm(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <div className="space-y-4">
                <select
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newContribution.harvest_event_id}
                  onChange={e => setNewContribution(prev => ({ ...prev, harvest_event_id: e.target.value }))}
                >
                  <option value="">Select Event (optional)</option>
                  {harvestEvents.filter(e => e.is_active).map(event => (
                    <option key={event.id} value={event.id}>{event.name}</option>
                  ))}
                </select>
                
                <select
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newContribution.harvest_type}
                  onChange={e => setNewContribution(prev => ({ ...prev, harvest_type: e.target.value }))}
                >
                  <option value="adult">Adult Harvest</option>
                  <option value="juvenile">Juvenile Harvest</option>
                </select>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search member by name or ID (M-0001)..."
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                    value={newContribution.contributor_name}
                    onChange={async (e) => {
                      const val = e.target.value;
                      setNewContribution(prev => ({ ...prev, contributor_name: val, contributor_id: '' }));
                      if (val.length >= 2) {
                        try {
                          const res = await axios.get(`${API}/members/search/${encodeURIComponent(val)}`);
                          setMemberSearchResults(res.data || []);
                        } catch { setMemberSearchResults([]); }
                      } else {
                        setMemberSearchResults([]);
                      }
                    }}
                    data-testid="contributor-search-input"
                  />
                  {newContribution.contributor_id && (
                    <span className="absolute right-3 top-3 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-bold">
                      {members.find(m => m.id === newContribution.contributor_id)?.member_number || 'Linked'}
                    </span>
                  )}
                  {memberSearchResults.length > 0 && !newContribution.contributor_id && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                      {memberSearchResults.map(m => (
                        <button
                          key={m.id}
                          className="w-full text-left px-4 py-2.5 hover:bg-amber-50 flex items-center justify-between border-b border-slate-50 last:border-0"
                          onClick={() => {
                            setNewContribution(prev => ({
                              ...prev,
                              contributor_type: 'member',
                              contributor_id: m.id,
                              contributor_name: m.full_name,
                              contributor_email: m.email || ''
                            }));
                            setMemberSearchResults([]);
                          }}
                          data-testid={`member-result-${m.id}`}
                        >
                          <div>
                            <span className="font-semibold text-slate-800">{m.full_name}</span>
                            {m.phone && <span className="text-xs text-slate-400 ml-2">{m.phone}</span>}
                          </div>
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">{m.member_number || '—'}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {newContribution.contributor_name.length >= 2 && memberSearchResults.length === 0 && !newContribution.contributor_id && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-amber-200 rounded-xl shadow-xl p-3">
                      <p className="text-sm text-slate-500 mb-2">No member found. Register as new member?</p>
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/members/register`, {
                              full_name: newContribution.contributor_name,
                              phone: '',
                              email: newContribution.contributor_email || ''
                            });
                            const newMember = res.data.member;
                            setMembers(prev => [newMember, ...prev]);
                            setNewContribution(prev => ({
                              ...prev,
                              contributor_type: 'member',
                              contributor_id: newMember.id,
                              contributor_name: newMember.full_name,
                              contributor_email: newMember.email || ''
                            }));
                            setMemberSearchResults([]);
                          } catch (err) {
                            alert('Failed to register member');
                          }
                        }}
                        className="w-full py-2 bg-amber-500 text-white rounded-lg font-medium text-sm hover:bg-amber-600"
                        data-testid="register-new-member-btn"
                      >
                        + Register "{newContribution.contributor_name}" as New Member
                      </button>
                    </div>
                  )}
                </div>

                <input
                  type="email"
                  placeholder="Email Address (for receipt)"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newContribution.contributor_email}
                  onChange={e => setNewContribution(prev => ({ ...prev, contributor_email: e.target.value }))}
                  data-testid="contributor-email-input"
                />

                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount (£)"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newContribution.amount}
                  onChange={e => setNewContribution(prev => ({ ...prev, amount: e.target.value }))}
                />

                <select
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newContribution.payment_method}
                  onChange={e => setNewContribution(prev => ({ ...prev, payment_method: e.target.value }))}
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>

                <input
                  type="date"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={newContribution.date}
                  onChange={e => setNewContribution(prev => ({ ...prev, date: e.target.value }))}
                />

                <textarea
                  placeholder="Notes (optional)"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  rows={2}
                  value={newContribution.notes}
                  onChange={e => setNewContribution(prev => ({ ...prev, notes: e.target.value }))}
                />

                <motion.button
                  onClick={handleCreateContribution}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Record & Generate Receipt
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Receipt Modal */}
      <AnimatePresence>
        {receiptModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setReceiptModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
              data-testid="receipt-modal"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Contribution Recorded!</h3>
                <p className="text-slate-500 text-sm mt-1">Receipt generated successfully</p>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Receipt No:</span>
                  <span className="font-mono font-bold text-slate-800">{receiptModal.contribution?.receipt_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Contributor:</span>
                  <span className="font-semibold text-slate-800">{receiptModal.contribution?.contributor_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Amount:</span>
                  <span className="font-bold text-emerald-600">£{receiptModal.contribution?.amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Payment:</span>
                  <span className="text-slate-800 capitalize">{receiptModal.contribution?.payment_method?.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Received By:</span>
                  <span className="text-slate-800">{receiptModal.contribution?.received_by_name}</span>
                </div>
                
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Mini Statement</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Previous Total:</span>
                    <span className="text-slate-800">£{receiptModal.mini_statement?.previous_total?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">This Contribution:</span>
                    <span className="text-emerald-600">+£{receiptModal.mini_statement?.current_contribution?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t border-slate-200 pt-2 mt-2">
                    <span className="text-slate-700">Total to Date:</span>
                    <span className="text-slate-800">£{receiptModal.mini_statement?.total_to_date?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {receiptModal.email_sent && (
                <div className="mt-4 px-4 py-2 bg-blue-50 rounded-xl flex items-center gap-2 text-blue-700 text-sm">
                  <Mail className="w-4 h-4" />
                  Receipt sent to contributor's email
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-6">
                <motion.button
                  onClick={() => setReceiptModal(null)}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Close
                </motion.button>
                <motion.button
                  onClick={() => {
                    const receiptNumber = receiptModal.contribution?.receipt_number;
                    if (receiptNumber) {
                      window.open(`${API}/harvest-contributions/receipt/${receiptNumber}/pdf`, '_blank');
                    }
                  }}
                  data-testid="download-pdf-btn"
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Download className="w-4 h-4" />
                  PDF
                </motion.button>
                <motion.button
                  onClick={() => {
                    const receiptNumber = receiptModal.contribution?.receipt_number;
                    if (receiptNumber) {
                      window.open(`${API}/harvest-contributions/receipt/${receiptNumber}/image`, '_blank');
                    }
                  }}
                  data-testid="download-image-btn"
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ImageIcon className="w-4 h-4" />
                  Image
                </motion.button>
                <motion.button
                  onClick={() => {
                    const receiptNumber = receiptModal.contribution?.receipt_number;
                    if (receiptNumber) {
                      const printWindow = window.open(`${API}/harvest-contributions/receipt/${receiptNumber}/pdf`, '_blank');
                      if (printWindow) {
                        printWindow.addEventListener('load', () => {
                          setTimeout(() => printWindow.print(), 500);
                        });
                      }
                    }
                  }}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Printer className="w-4 h-4" />
                  Print
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : activeTab === 'events' ? (
        <div className="space-y-4">
          {harvestEvents.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-slate-100">
              <Wheat className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-600 font-medium">No harvest events yet</p>
              <p className="text-sm text-slate-500 mt-1">Create your first harvest event to start tracking contributions</p>
            </div>
          ) : (
            harvestEvents.map(event => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl ${event.harvest_type === 'juvenile' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                      <Wheat className={`w-6 h-6 ${event.harvest_type === 'juvenile' ? 'text-blue-600' : 'text-amber-600'}`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800">{event.name}</h3>
                      <p className="text-sm text-slate-500 capitalize">{event.harvest_type} Harvest • {event.date}</p>
                      {event.description && <p className="text-sm text-slate-600 mt-1">{event.description}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    {event.target_amount > 0 && (
                      <p className="text-sm text-slate-500">Target: £{event.target_amount.toLocaleString()}</p>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      event.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {event.is_active ? 'Active' : 'Closed'}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search */}
          <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or receipt number..."
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-amber-400 outline-none"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Contributions List */}
          {filteredContributions.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-slate-100">
              <Receipt className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-600 font-medium">No contributions yet</p>
              <p className="text-sm text-slate-500 mt-1">Record contributions to generate receipts</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredContributions.map(contribution => (
                <motion.div
                  key={contribution.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-4 shadow-lg border border-slate-100 hover:shadow-xl transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-emerald-100">
                        <Receipt className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{contribution.contributor_name}</p>
                        <p className="text-sm text-slate-500">
                          {contribution.receipt_number} • {contribution.date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        <button
                          onClick={() => window.open(`${API}/harvest-contributions/receipt/${contribution.receipt_number}/pdf`, '_blank')}
                          className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                          title="Download PDF"
                          data-testid={`redownload-pdf-${contribution.receipt_number}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => window.open(`${API}/harvest-contributions/receipt/${contribution.receipt_number}/image`, '_blank')}
                          className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                          title="Download Image"
                          data-testid={`redownload-img-${contribution.receipt_number}`}
                        >
                          <ImageIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">£{contribution.amount?.toLocaleString()}</p>
                        <p className="text-xs text-slate-500 capitalize">{contribution.payment_method?.replace('_', ' ')}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Income Management Component
const IncomeManagement = ({ adminUser }) => {
  const [incomeEntries, setIncomeEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editRequests, setEditRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(null);
  const [requestReason, setRequestReason] = useState('');
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: 'tithe',
    notes: '',
    reference: ''
  });

  const isMainAdmin = adminUser?.role === 'main_admin';

  const loadData = async () => {
    setLoading(true);
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/income`),
        axios.get(`${API}/income/summary`)
      ]);
      setIncomeEntries(entriesRes.data);
      setSummary(summaryRes.data);
      
      // Load edit requests for main admin
      if (isMainAdmin) {
        try {
          const requestsRes = await axios.get(`${API}/finance/edit-requests?type=income`);
          setEditRequests(requestsRes.data || []);
        } catch (e) {
          console.log('Edit requests endpoint not available');
        }
      }
    } catch (error) {
      console.error('Error loading income data:', error);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [isMainAdmin]);

  const handleAddEntry = async () => {
    if (!newEntry.amount || !newEntry.date) return;
    try {
      await axios.post(`${API}/income`, {
        ...newEntry,
        amount: parseFloat(newEntry.amount),
        created_by: adminUser?.username || 'admin'
      });
      setNewEntry({ date: new Date().toISOString().split('T')[0], amount: '', category: 'tithe', notes: '', reference: '' });
      setShowAddForm(false);
      loadData();
    } catch (error) {
      console.error('Error adding income entry:', error);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!isMainAdmin) return;
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await axios.delete(`${API}/income/${entryId}`);
      loadData();
    } catch (error) {
      console.error('Error deleting income entry:', error);
    }
  };

  const handleRequestEdit = async (entry) => {
    if (!requestReason.trim()) {
      alert('Please provide a reason for the edit request');
      return;
    }
    try {
      await axios.post(`${API}/finance/edit-requests`, {
        entry_type: 'income',
        entry_id: entry.id,
        entry_data: entry,
        requested_by: adminUser?.username || 'sub_admin',
        reason: requestReason
      });
      alert('Edit request submitted successfully. Main admin will review it.');
      setShowRequestModal(null);
      setRequestReason('');
    } catch (error) {
      console.error('Error submitting edit request:', error);
      alert('Failed to submit request. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Income Management</h2>
              <p className="text-sm text-slate-500">Track tithes, offerings, and donations</p>
            </div>
          </div>
          <motion.button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium shadow-lg flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-4 h-4" />
            Add Income
          </motion.button>
        </div>

        {/* Add Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200"
            >
              <div className="grid gap-4 md:grid-cols-3">
                <input
                  type="date"
                  className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 outline-none"
                  value={newEntry.date}
                  onChange={e => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount (£)"
                  className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 outline-none"
                  value={newEntry.amount}
                  onChange={e => setNewEntry(prev => ({ ...prev, amount: e.target.value }))}
                />
                <select
                  className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 outline-none"
                  value={newEntry.category}
                  onChange={e => setNewEntry(prev => ({ ...prev, category: e.target.value }))}
                >
                  {INCOME_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <input
                  placeholder="Reference (optional)"
                  className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 outline-none"
                  value={newEntry.reference}
                  onChange={e => setNewEntry(prev => ({ ...prev, reference: e.target.value }))}
                />
                <input
                  placeholder="Notes (optional)"
                  className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 outline-none md:col-span-2"
                  value={newEntry.notes}
                  onChange={e => setNewEntry(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
              <motion.button
                onClick={handleAddEntry}
                className="mt-4 w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                Save Entry
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200">
            <p className="text-sm text-emerald-700">Total</p>
            <p className="text-2xl font-bold text-emerald-900">£{(summary.grand_total || 0).toLocaleString()}</p>
          </div>
          {INCOME_CATEGORIES.slice(0, 4).map(cat => (
            <div key={cat.value} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
              <p className="text-sm text-slate-600">{cat.label}</p>
              <p className="text-xl font-bold text-slate-800">£{(summary.by_category?.[cat.value] || 0).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Entries List */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <h3 className="font-semibold text-slate-800 mb-4">Recent Entries ({incomeEntries.length})</h3>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : incomeEntries.length === 0 ? (
          <div className="text-center py-12">
            <PoundSterling className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600">No income entries yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {incomeEntries.slice(0, 20).map((entry, index) => {
              const cat = INCOME_CATEGORIES.find(c => c.value === entry.category) || INCOME_CATEGORIES[4];
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="p-4 rounded-xl bg-slate-50 border border-slate-200 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-emerald-500">
                        <PoundSterling className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">£{entry.amount?.toLocaleString()}</p>
                        <p className="text-sm text-slate-600">{cat.label} • {entry.date}</p>
                        {entry.notes && <p className="text-xs text-slate-500 mt-1">{entry.notes}</p>}
                        {entry.recorded_by && (
                          <p className="text-xs text-blue-600 mt-1">Entered by: {entry.recorded_by}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.reference && (
                        <span className="px-3 py-1 rounded-full bg-slate-200 text-slate-700 text-xs font-medium">
                          Ref: {entry.reference}
                        </span>
                      )}
                      {isMainAdmin ? (
                        <button
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                          title="Delete Entry"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setShowRequestModal(entry)}
                          className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 text-xs font-medium transition-colors"
                          title="Request Edit"
                        >
                          Request Change
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Request Edit Modal for Sub-Admins */}
      <AnimatePresence>
        {showRequestModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRequestModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-slate-800 mb-4">Request Financial Edit</h3>
              <div className="mb-4 p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-600">Entry Details:</p>
                <p className="font-semibold text-slate-800">£{showRequestModal.amount?.toLocaleString()} - {showRequestModal.category}</p>
                <p className="text-xs text-slate-500">{showRequestModal.date}</p>
              </div>
              <p className="text-sm text-amber-600 mb-4">
                Financial entries cannot be edited directly. Please provide a reason for the change request and the main admin will review it.
              </p>
              <textarea
                placeholder="Reason for edit request (e.g., wrong amount entered, incorrect date...)"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none mb-4"
                rows={3}
                value={requestReason}
                onChange={e => setRequestReason(e.target.value)}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowRequestModal(null); setRequestReason(''); }}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRequestEdit(showRequestModal)}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold"
                >
                  Submit Request
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Expenses Management Component
const ExpensesManagement = ({ adminUser }) => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(null);
  const [requestReason, setRequestReason] = useState('');
  const [newExpense, setNewExpense] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: 'utilities',
    description: ''
  });

  const isMainAdmin = adminUser?.role === 'main_admin';

  const loadData = async () => {
    setLoading(true);
    try {
      const [expensesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/expenses`),
        axios.get(`${API}/expenses/summary`)
      ]);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleAddExpense = async () => {
    if (!newExpense.amount || !newExpense.date) return;
    try {
      await axios.post(`${API}/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount),
        created_by: adminUser?.username || 'admin'
      });
      setNewExpense({ date: new Date().toISOString().split('T')[0], amount: '', category: 'utilities', description: '' });
      setShowAddForm(false);
      loadData();
    } catch (error) {
      console.error('Error adding expense:', error);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!isMainAdmin) return;
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      loadData();
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const handleRequestEdit = async (entry) => {
    if (!requestReason.trim()) {
      alert('Please provide a reason for the edit request');
      return;
    }
    try {
      await axios.post(`${API}/finance/edit-requests`, {
        entry_type: 'expense',
        entry_id: entry.id,
        entry_data: entry,
        requested_by: adminUser?.username || 'sub_admin',
        reason: requestReason
      });
      alert('Edit request submitted successfully. Main admin will review it.');
      setShowRequestModal(null);
      setRequestReason('');
    } catch (error) {
      console.error('Error submitting edit request:', error);
      alert('Failed to submit request. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-rose-500">
              <TrendingDown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Expenses Management</h2>
              <p className="text-sm text-slate-500">Track church expenses and outflows</p>
            </div>
          </div>
          <motion.button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-medium shadow-lg flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-4 h-4" />
            Add Expense
          </motion.button>
        </div>

        {/* Add Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 p-4 rounded-2xl bg-red-50 border border-red-200"
            >
              <div className="grid gap-4 md:grid-cols-3">
                <input
                  type="date"
                  className="border-2 border-red-200 rounded-xl px-4 py-3 focus:border-red-400 outline-none"
                  value={newExpense.date}
                  onChange={e => setNewExpense(prev => ({ ...prev, date: e.target.value }))}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount (£)"
                  className="border-2 border-red-200 rounded-xl px-4 py-3 focus:border-red-400 outline-none"
                  value={newExpense.amount}
                  onChange={e => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                />
                <select
                  className="border-2 border-red-200 rounded-xl px-4 py-3 focus:border-red-400 outline-none"
                  value={newExpense.category}
                  onChange={e => setNewExpense(prev => ({ ...prev, category: e.target.value }))}
                >
                  {EXPENSE_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <input
                  placeholder="Description"
                  className="border-2 border-red-200 rounded-xl px-4 py-3 focus:border-red-400 outline-none md:col-span-3"
                  value={newExpense.description}
                  onChange={e => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <motion.button
                onClick={handleAddExpense}
                className="mt-4 w-full py-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-semibold"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                Save Expense
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-2xl bg-gradient-to-br from-red-50 to-rose-50 border border-red-200">
            <p className="text-sm text-red-700">Total Expenses</p>
            <p className="text-2xl font-bold text-red-900">£{(summary.total || 0).toLocaleString()}</p>
          </div>
          {EXPENSE_CATEGORIES.slice(0, 3).map(cat => (
            <div key={cat.value} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
              <p className="text-sm text-slate-600">{cat.label}</p>
              <p className="text-xl font-bold text-slate-800">£{(summary.by_category?.[cat.value] || 0).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Expenses List */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <h3 className="font-semibold text-slate-800 mb-4">Recent Expenses ({expenses.length})</h3>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
          </div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-12">
            <TrendingDown className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600">No expenses recorded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {expenses.slice(0, 20).map((expense, index) => {
              const cat = EXPENSE_CATEGORIES.find(c => c.value === expense.category) || EXPENSE_CATEGORIES[5];
              return (
                <motion.div
                  key={expense.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="p-4 rounded-xl bg-slate-50 border border-slate-200 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-red-500">
                        <TrendingDown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">£{expense.amount?.toLocaleString()}</p>
                        <p className="text-sm text-slate-600">{cat.label} • {expense.date}</p>
                        {expense.description && <p className="text-xs text-slate-500 mt-1">{expense.description}</p>}
                        {expense.recorded_by && (
                          <p className="text-xs text-blue-600 mt-1">Entered by: {expense.recorded_by}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isMainAdmin ? (
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                          title="Delete Expense"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setShowRequestModal(expense)}
                          className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 text-xs font-medium transition-colors"
                          title="Request Edit"
                        >
                          Request Change
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Request Edit Modal for Sub-Admins */}
      <AnimatePresence>
        {showRequestModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRequestModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-slate-800 mb-4">Request Financial Edit</h3>
              <div className="mb-4 p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-600">Expense Details:</p>
                <p className="font-semibold text-slate-800">£{showRequestModal.amount?.toLocaleString()} - {showRequestModal.category}</p>
                <p className="text-xs text-slate-500">{showRequestModal.date}</p>
              </div>
              <p className="text-sm text-amber-600 mb-4">
                Financial entries cannot be edited directly. Please provide a reason for the change request and the main admin will review it.
              </p>
              <textarea
                placeholder="Reason for edit request (e.g., wrong amount entered, incorrect category...)"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none mb-4"
                rows={3}
                value={requestReason}
                onChange={e => setRequestReason(e.target.value)}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowRequestModal(null); setRequestReason(''); }}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRequestEdit(showRequestModal)}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold"
                >
                  Submit Request
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Finance Audit Log Component - Shows who entered what and when
const FinanceAuditLog = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, income, expense

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' 
        ? `${API}/finance/audit-log?limit=100`
        : `${API}/finance/audit-log?limit=100&entry_type=${filter}`;
      const response = await axios.get(url);
      setAuditLogs(response.data || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    }
    setLoading(false);
  };

  useEffect(() => { loadAuditLogs(); }, [filter]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Financial Audit Log</h2>
            <p className="text-sm text-slate-500">Track all financial entries and changes</p>
          </div>
          <div className="flex gap-2">
            {['all', 'income', 'expense'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  filter === f
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {f === 'all' ? 'All' : f === 'income' ? 'Income' : 'Expenses'}
              </button>
            ))}
          </div>
        </div>

        {/* Audit Log Table */}
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="text-center py-12">
            <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600">No audit logs found</p>
            <p className="text-sm text-slate-400">Financial activities will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Date & Time</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Action</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Category</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Performed By</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log, index) => (
                  <tr key={log.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-600">{formatDate(log.timestamp)}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        log.action === 'create' ? 'bg-green-100 text-green-700' :
                        log.action === 'delete' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {log.action?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        log.entry_type === 'income' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {log.entry_type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm font-semibold text-slate-800">
                      £{log.amount?.toLocaleString() || '0'}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{log.category || '-'}</td>
                    <td className="py-3 px-4">
                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                        {log.performed_by || 'Unknown'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-500 max-w-xs truncate">{log.details || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Finance Section Component
const FinanceSection = ({ adminUser }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/finance/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading finance dashboard:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard();
    }
  }, [activeTab]);

  return (
    <div className="space-y-6" data-testid="finance-section">
      {/* Header with tabs */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
        <div className="flex gap-2 overflow-x-auto">
          {FINANCE_TABS.filter(tab => !tab.mainAdminOnly || adminUser?.role === 'main_admin').map(tab => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'dashboard' && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <FinanceDashboard data={dashboardData} loading={loading} onRefresh={loadDashboard} />
          </motion.div>
        )}
        {activeTab === 'harvest' && (
          <motion.div
            key="harvest"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <HarvestManagement adminUser={adminUser} />
          </motion.div>
        )}
        {activeTab === 'income' && (
          <motion.div
            key="income"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <IncomeManagement adminUser={adminUser} />
          </motion.div>
        )}
        {activeTab === 'expenses' && (
          <motion.div
            key="expenses"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <ExpensesManagement adminUser={adminUser} />
          </motion.div>
        )}
        {activeTab === 'reports' && (
          <motion.div
            key="reports"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <FinancialReportsSection />
          </motion.div>
        )}
        {activeTab === 'audit' && adminUser?.role === 'main_admin' && (
          <motion.div
            key="audit"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <FinanceAuditLog />
          </motion.div>
        )}
        {activeTab === 'email' && (
          <motion.div
            key="email"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <DepartmentEmailModule department="finance" adminUser={adminUser} compact={false} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FinanceSection;
